// frontend/src/components/RealtimeAssistant.jsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import webSocketRealtimeClient from "./RealtimeClient";
import { BACKEND_BASE_URL } from "../config.js";
// Removed admin UI imports - keeping UI consistent across all modes
import AdminAuth from "./auth/AdminAuth";
import { authManager, isAdminAuthenticated } from "../utils/authManager";

/**
 * Realtime Voice Assistant - Polished UI
 *
 * Clean, modern interface with chat bubbles and central microphone
 */
export default function RealtimeAssistant({ 
  embedded = false, 
  backendUrl = null, 
  instructions = null, 
  initialStrategy = "auto",
  backgroundMode = false,
  onMicrophoneStateChange = null
}) {
  // Helper function to get the appropriate backend URL
  const getBackendUrl = () => {
    return backendUrl || BACKEND_BASE_URL;
  };
  // State
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [listening, setListening] = useState(false);
  const [error, setError] = useState(null);
  const [messages, setMessages] = useState([]);
  const [currentResponse, setCurrentResponse] = useState("");
  const [toolStrategy, setToolStrategy] = useState(initialStrategy);
  const [strategySource, setStrategySource] = useState("default");
  const [showMessages, setShowMessages] = useState(false);
  const [toast, setToast] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [userSpeaking, setUserSpeaking] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  // Video state - enabled by default to show avatar
  const [videoMode, setVideoMode] = useState(true);
  // Admin/visitor mode state
  const [currentMode, setCurrentMode] = useState("visitor");
  // Authentication state
  const [showAuthOverlay, setShowAuthOverlay] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [videoWebSocket, setVideoWebSocket] = useState(null);
  const [peerConnection, setPeerConnection] = useState(null);
  const [videoLatency, setVideoLatency] = useState(null);
  const idleVideoRef = useRef(null);
  const streamVideoRef = useRef(null);

  // User state
  const [userName, setUserName] = useState(
    localStorage.getItem("userName") || ""
  );
  const [userUuid] = useState(() => {
    let uuid = localStorage.getItem("uuid");
    if (!uuid) {
      uuid = crypto.randomUUID();
      localStorage.setItem("uuid", uuid);
    }
    return uuid;
  });

  const [isClearing, setIsClearing] = useState(false);
  const [sessionData, setSessionData] = useState(null);

  // Audio visualization
  const [audioLevels, setAudioLevels] = useState([]);
  const [isInterrupted, setIsInterrupted] = useState(false);

  // Refs
  const messagesEndRef = useRef(null);
  const audioContextRef = useRef(null);
  const animationRef = useRef(null);
  const clientRef = useRef(webSocketRealtimeClient);
  const videoModeRef = useRef(videoMode);
  const sessionHandledRef = useRef(false);
  const statsIntervalRef = useRef(null);
  const lastBytesReceivedRef = useRef(0);
  const videoStreamReadyRef = useRef(false);
  const speechStoppedTimeRef = useRef(null);
  const videoInterruptedRef = useRef(false);
  const adminAuthRef = useRef(null);
  const currentModeRef = useRef(currentMode);
  const speakingRef = useRef(speaking);

  function playIdleVideo() {
    // Source is already set in JSX
    if (idleVideoRef.current) {
      idleVideoRef.current.play().catch((e) => {
        console.log("Idle video play failed:", e);
      });
    }
  }

  // Helper to manage video transitions and button state
  function showStreamVideo() {
    console.log("🎬 Showing stream video - setting speaking=true");
    if (streamVideoRef.current) streamVideoRef.current.style.opacity = "1";
    if (idleVideoRef.current) idleVideoRef.current.style.opacity = "0";
    setSpeaking(true);
    setIsProcessing(false);
  }

  function showIdleVideo() {
    console.log("🎬 Showing idle video - setting speaking=false");
    if (streamVideoRef.current) streamVideoRef.current.style.opacity = "0";
    if (idleVideoRef.current) idleVideoRef.current.style.opacity = "1";
    setSpeaking(false);
  }

  function setStreamVideo(stream) {
    // Always set the stream - we'll control playback separately
    console.log("🎬 Setting stream video source", {
      stream,
      hasVideoTracks: stream.getVideoTracks().length,
      videoTracks: stream.getVideoTracks().map(t => ({id: t.id, enabled: t.enabled, readyState: t.readyState}))
    });
    
    streamVideoRef.current.srcObject = stream;
    streamVideoRef.current.loop = false; // Prevent looping of response videos
    
    // Safari hotfix - ensure video plays
    if (streamVideoRef.current.paused) {
      streamVideoRef.current.play()
        .then(() => console.log("🎬 Safari hotfix: video playing"))
        .catch(err => console.log("🎬 Safari hotfix: play failed", err));
    }

    streamVideoRef.current.onloadedmetadata = () => {
      console.log("🎬 Stream video metadata loaded");
      // Explicitly try to play
      streamVideoRef.current.play().then(() => {
        console.log("🎬 Stream video play() succeeded");
        // Force visibility on successful play
        showStreamVideo();
      }).catch(err => {
        console.error("🎬 Stream video play() failed:", err);
      });
    };

    streamVideoRef.current.onplaying = () => {
      console.log("🎬 Stream video is playing");
      
      if (speechStoppedTimeRef.current) {
        const latency = (Date.now() - speechStoppedTimeRef.current) / 1000;
        setVideoLatency(latency);
        console.log(`⏱️ Video latency (speech stop→display): ${latency.toFixed(2)}s`);
        speechStoppedTimeRef.current = null;
      }
      // We'll set speaking state when opacity changes, not here
    };

    streamVideoRef.current.onended = () => {
      console.log("🎬 Stream video ended");
      // Switch back to idle
      showIdleVideo();
    };

    streamVideoRef.current.onpause = () => {
      console.log("🎬 Stream video paused");
      // Only clear speaking if we're not interrupted (user didn't interrupt)
      if (!videoInterruptedRef.current) {
        showIdleVideo();
      }
    };

    streamVideoRef.current.onwaiting = () => {
      console.log("🎬 Stream video is waiting/buffering");
    };

    streamVideoRef.current
      .play()
      .then(() => {
        console.log("🎬 Stream video play() succeeded");
      })
      .catch((error) => {
        console.error("🎬 Stream video play() failed:", error);
      });

    console.log("🎬 Stream video element set, waiting for data...");
  }

  // Auto-scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(scrollToBottom, [messages, currentResponse]);

  // Initialize UUID
  useEffect(() => {
    localStorage.setItem("uuid", userUuid);
  }, [userUuid]);

  useEffect(() => {
    localStorage.setItem("userName", userName);
  }, [userName]);

  useEffect(() => {
    videoModeRef.current = videoMode;
  }, [videoMode]);

  // Keep currentModeRef in sync
  useEffect(() => {
    currentModeRef.current = currentMode;
  }, [currentMode]);

  // Keep speakingRef in sync
  useEffect(() => {
    speakingRef.current = speaking;
  }, [speaking]);

  // Set backend URL when backendUrl prop changes
  useEffect(() => {
    if (backendUrl && clientRef.current) {
      clientRef.current.setBackendUrl(backendUrl);
    }
  }, [backendUrl]);

  // Check for existing authentication on mount
  useEffect(() => {
    const checkExistingAuth = async () => {
      try {
        // Check if we have stored auth data
        const storedAuth = localStorage.getItem('adminAuth');
        if (storedAuth) {
          const authData = JSON.parse(storedAuth);
          
          // Check if token is still valid (not expired)
          // Try to decode the JWT to get actual expiry
          let isExpired = false;
          try {
            if (authData.idToken) {
              // Decode JWT payload (base64)
              const payload = JSON.parse(atob(authData.idToken.split('.')[1]));
              const exp = payload.exp;
              const now = Math.floor(Date.now() / 1000); // Current time in seconds
              isExpired = now >= exp;
              console.log(`🔑 Token expiry check: exp=${exp}, now=${now}, expired=${isExpired}`);
            }
          } catch (e) {
            console.log('🔑 Could not decode token, using timestamp fallback');
            // Fallback to timestamp check
            const tokenExpiry = authData.timestamp + (60 * 60 * 1000); // 1 hour expiry
            const now = Date.now();
            isExpired = now >= tokenExpiry;
          }
          
          if (!isExpired && authData.accessToken) {
            console.log('🔑 Found valid stored authentication');
            
            // Update auth manager with stored data
            authManager.setAuthData({
              idToken: authData.idToken,
              accessToken: authData.accessToken,
              refreshToken: authData.refreshToken,
              phone: authData.phone,
              tenantId: authData.tenantId
            });
            
            // CRITICAL: Set auth token on the client BEFORE any connection
            if (authData.idToken && clientRef.current) {
              console.log('🔑 Setting auth token on RealtimeClient');
              clientRef.current.setAuthToken(authData.idToken);
            }
            
            // Set authenticated state
            setIsAuthenticated(true);
            
            // If we're connected, notify backend of existing auth
            if (connected && clientRef.current) {
              console.log('🔑 Sending stored auth to backend');
              clientRef.current.sendEvent({
                type: 'auth.event',
                event_type: 'auth_restored',
                data: authData
              });
            }
          } else {
            console.log('🔑 Stored auth expired or invalid:', {
              isExpired,
              hasAccessToken: !!authData.accessToken,
              hasIdToken: !!authData.idToken
            });
            localStorage.removeItem('adminAuth');
          }
        }
      } catch (error) {
        console.error('❌ Error checking existing auth:', error);
      } finally {
        setCheckingAuth(false);
      }
    };
    
    checkExistingAuth();
  }, []);

  // Re-send auth when connection is established ONLY if backend doesn't already know
  useEffect(() => {
    if (connected && isAuthenticated && !checkingAuth) {
      // Small delay to ensure initial state is received
      setTimeout(() => {
        // Check if we're already in admin mode from initial state (using ref for current value)
        if (currentModeRef.current === 'admin') {
          console.log('🔑 Already in admin mode, no need to send auth_restored');
          return;
        }
        
        const storedAuth = localStorage.getItem('adminAuth');
        if (storedAuth) {
          try {
            const authData = JSON.parse(storedAuth);
            console.log('🔑 Connection established but not in admin mode, sending auth to backend');
            clientRef.current.sendEvent({
              type: 'auth.event',
              event_type: 'auth_restored',
              data: authData
            });
          } catch (error) {
            console.error('❌ Error sending auth on connect:', error);
          }
        }
      }, 500); // Wait 500ms for initial state to be received
    }
  }, [connected, isAuthenticated, checkingAuth]); // Remove currentMode from dependencies to avoid re-runs

  useEffect(() => {
    if (webSocketRealtimeClient) {
      // Always enable audio for interruption detection, even in video mode
      webSocketRealtimeClient.setAudioEnabled(true);
    }
  }, [videoMode]);

  useEffect(() => {
    // Preload idle video on mount
    if (idleVideoRef.current) {
      idleVideoRef.current.load();
      console.log("🎬 Preloading idle video...");

      idleVideoRef.current.oncanplaythrough = () => {
        console.log("✅ Idle video preloaded and ready");
        // Debug: Check video element state
        console.log("🎬 Idle video debug:", {
          opacity: idleVideoRef.current.style.opacity,
          display: window.getComputedStyle(idleVideoRef.current).display,
          visibility: window.getComputedStyle(idleVideoRef.current).visibility,
          width: idleVideoRef.current.offsetWidth,
          height: idleVideoRef.current.offsetHeight,
          parentDisplay: idleVideoRef.current.parentElement ? window.getComputedStyle(idleVideoRef.current.parentElement).display : 'no parent'
        });
      };
      
      idleVideoRef.current.onerror = (e) => {
        console.error("❌ Idle video error:", e);
      };
    }
  }, []);


  // Initialize client and event listeners
  useEffect(() => {
    const client = clientRef.current;
    
    // Make client globally accessible for AdminAuth component
    window.webSocketRealtimeClient = client;
    console.log('🌐 Set window.webSocketRealtimeClient for global access');

    // Set up event listeners using the working logic
    client.on("connected", handleConnected);
    client.on("session.created", handleSessionCreated);
    client.on("disconnected", handleDisconnected);
    client.on("error", handleError);
    
    // Admin intent detection event
    client.on("admin_intent_detected", (event) => {
      console.log('🎯 Admin intent detected from backend:', event);
      if (currentMode === 'visitor' && !isAuthenticated) {
        console.log('🔑 Showing authentication UI');
        setShowAuthOverlay(true);
        showToast('Admin access requires authentication', 'info');
      }
    });
    
    // Optional: Handle low confidence intent detection
    client.on("admin_intent_detected_low_confidence", (event) => {
      console.log('🤔 Low confidence admin intent detected:', event);
      // Could show a prompt like "Did you want to access admin features?"
    });
    
    // Handle auth tool events from backend
    client.on("auth.tool", (event) => {
      console.log('🔧 Auth tool event received:', event);
      const { tool, args } = event;
      
      // Handle show_signin_form specially as it needs to show the overlay
      if (tool === 'show_signin_form') {
        console.log('🔑 Showing auth UI from tool event');
        setShowAuthOverlay(true);
        return; // Don't process further for this tool
      }
      
      // Store pending auth tool events if overlay isn't ready
      const executeTool = () => {
        if (!adminAuthRef.current) {
          console.log('⏳ AdminAuth ref not ready, retrying in 100ms...');
          setTimeout(executeTool, 100);
          return;
        }
        
        console.log('🔧 Executing auth tool:', tool, 'with args:', args);
        
        switch (tool) {
          case 'set_phone_number':
            if (args && args.phoneNumber) {
              console.log('📱 Setting phone number to:', args.phoneNumber);
              adminAuthRef.current.setPhone(args.phoneNumber);
            } else {
              console.error('❌ Missing phoneNumber in args:', args);
            }
            break;
            
          case 'set_otp_code':
            if (args && args.code) {
              console.log('🔑 Setting OTP code to:', args.code);
              adminAuthRef.current.setCode(args.code);
            } else {
              console.error('❌ Missing code in args:', args);
            }
            break;
            
          case 'submit_phone_number':
            console.log('📤 Submitting phone number form');
            adminAuthRef.current.sendOtp();
            break;
              
            case 'submit_otp_verification':
              console.log('✅ Submitting OTP verification form');
              adminAuthRef.current.verifyOtp();
              break;
              
            default:
              console.warn('⚠️ Unknown auth tool:', tool);
          }
      };
      
      // Always try to execute the tool - it will retry if needed
      executeTool();
    });
    client.on("message.completed", handleMessageCompleted);
    client.on("response.text.delta", handleTextDelta);
    client.on("speech.started", handleSpeechStarted);
    client.on("speech.stopped", handleSpeechStopped);
    client.on("response.audio.delta", handleAudioDelta);
    client.on("response.audio.done", handleAudioDone);
    // Don't listen to response.done - it fires too early before video completes
    // client.on("response.done", handleResponseDone);
    client.on("strategy_updated", handleStrategyUpdated);
    client.on("strategy_update_broadcast", handleStrategyUpdateBroadcast);
    client.on("did_talk_generated", handleDidTalkGenerated);
    client.on("did_talk_error", handleDidTalkError);
    client.on("assistant.interrupted", handleAssistantInterrupted);
    client.on("response.created", handleResponseCreated);
    client.on("mode.changed", handleModeChanged);
    client.on("mode.status", handleModeStatus);
    client.on("initial_state", handleInitialState);
    client.on("auth_complete", handleAuthComplete);

    // Auth tools are now handled entirely by the backend auth agent
    // Frontend no longer needs to define or intercept auth tools

    // Cleanup on unmount
    return () => {
      client.removeAllListeners();
      client.disconnect();
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      // Clean up global reference
      if (window.webSocketRealtimeClient === client) {
        window.webSocketRealtimeClient = null;
      }
    };
  }, []);

  // Event handlers - using working logic
  const handleConnected = () => {
    console.log("Connected to backend");
    // Don't change connecting state here - it will be set to false by session.created
  };

  const handleStrategyUpdated = (event) => {
    console.log("🎛️ Strategy updated:", event);
    setToolStrategy(event.strategy);
    setStrategySource(event.source || "direct");

    // Show user feedback about strategy change
    if (event.source === "dashboard_broadcast") {
      // This came from the dashboard - show special notification
      showStrategyChangeNotification(event.strategy, true);
    } else {
      // This was a direct update
      showStrategyChangeNotification(event.strategy, false);
    }
  };

  const handleDidTalkGenerated = (event) => {
    console.log("🎬 D-ID video received:", event);
    
    try {
      // If we receive new D-ID video, we're no longer interrupted
      if (isInterrupted) {
        console.log("🎬 New D-ID video received - clearing interrupted state");
        setIsInterrupted(false);
      }
      
      // Don't set speaking here - wait for actual video playback
      console.log("🎬 Video generated, waiting for playback to start");
      setIsProcessing(true); // Stay in processing until video actually plays
      
      // Clear interrupted flag - we have new content
      videoInterruptedRef.current = false;
      
      
      // Don't clear the video source here - the WebRTC stream is already established
      // Just prepare the video element for playback
      if (streamVideoRef.current) {
        console.log("🎬 Preparing stream video for new content");
        // If we have a srcObject, ensure the video is ready to play
        if (streamVideoRef.current.srcObject) {
          console.log("🎬 Stream video has srcObject, ensuring playback");
          // Make sure video isn't paused from a previous interruption
          if (streamVideoRef.current.paused) {
            streamVideoRef.current.play().catch(err => {
              console.error("🎬 Failed to play stream video:", err);
            });
          }
        }
      }
      
      // Ensure we have a video stream (this will skip if already exists)
      createVideoStream();
      
      console.log("🎬 Ready for new video playback");
    } catch (error) {
      console.error("🎬 Error in handleDidTalkGenerated:", error);
    }
  };

  const handleDidTalkError = (event) => {
    console.error("❌ D-ID video error:", event);
    setIsProcessing(false); // Clear processing state on error
    setSpeaking(false); // Clear speaking state on error
    showToast(`Video generation failed: ${event.error}`, "error");
  };
  
  const handleResponseCreated = (event) => {
    console.log("🔄 Response created");
    // Don't set speaking immediately - wait for video generation
    // The processing state should already be set from handleSpeechStopped
  };

  const handleStrategyUpdateBroadcast = (event) => {
    console.log("📡 Strategy broadcast received:", event);
    // This is handled the same as regular strategy updates
    handleStrategyUpdated(event);
  };

  async function handleSessionCreated(session) {
    if (sessionHandledRef.current) {
      console.log("🎬 Session already handled, skipping");
      return;
    }
    sessionHandledRef.current = true;

    console.log("Session ready:", session);
    setConnected(true);
    setConnecting(false);
    setError(null);

    // Store audio context reference
    audioContextRef.current = clientRef.current.audioContext;

    // Resume audio context on user gesture (required by browsers)
    if (audioContextRef.current) {
      await audioContextRef.current.resume();
      console.log("🎧 AudioContext resumed");
    }

    setListening(true);

    // Send existing authentication if available
    if (isAuthenticated && !checkingAuth) {
      const storedAuth = localStorage.getItem('adminAuth');
      if (storedAuth) {
        try {
          const authData = JSON.parse(storedAuth);
          console.log('🔑 Sending restored auth to backend after connection');
          clientRef.current.sendEvent({
            type: 'auth.event',
            event_type: 'auth_restored',
            data: authData
          });
        } catch (error) {
          console.error('❌ Error sending restored auth:', error);
        }
      }
    }

    // Send current video mode preference using ref value
    if (videoModeRef.current) {
      console.log("🎬 Sending initial video mode preference:", {
        enabled: videoModeRef.current,
      });
      webSocketRealtimeClient.sendEvent({
        type: "update_video_mode",
        enabled: videoModeRef.current,
      });

      // Create video stream if video mode was enabled before connection
      console.log("🎬 Video mode is enabled, creating stream...");
      createVideoStream();
    }

    // Auth tools are now handled entirely by the backend
    // No need to set up client-side auth tools
    console.log("🔧 Skipping client-side auth setup - backend handles all auth");
  }

  const handleDisconnected = () => {
    setConnected(false);
    setConnecting(false);
    setSpeaking(false);
    setListening(false);
    // Clear any residual errors on disconnect
    setError(null);
  };

  const handleError = (error) => {
    console.error("Client error:", error);
    const errMsg =
      typeof error === "string"
        ? error
        : error && error.message
        ? error.message
        : JSON.stringify(error);
    setError(errMsg);
    setConnecting(false);
  };

  const handleMessageCompleted = ({ role, text }) => {
    console.log(`UI: Message completed - role=${role}, text=${text}`);

    const newMessage = {
      role,
      text,
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, newMessage]);

    // Extract user name from any message if the user explicitly provides it
    if (role === "user" && text.length > 0) {
      extractUserName(text);
      
      // Check for admin authentication triggers
      checkForAdminAuthTriggers(text);
    }

    // Clear current response
    setCurrentResponse("");
  };

  // Handle text delta (streaming text as it's generated)
  const handleTextDelta = (delta) => {
    setCurrentResponse((prev) => prev + delta);
  };

  const handleAudioDelta = (event) => {
    setSpeaking(true);
    // Simple audio visualization - create random-ish levels based on audio activity
    if (speaking) {
      const levels = Array.from(
        { length: 12 },
        () => Math.random() * 0.7 + 0.3
      );
      setAudioLevels(levels);
    }
  };

  const handleAudioDone = () => {
    console.log("🔊 Audio done event");
    // In video mode, don't set speaking to false - we're waiting for video
    if (!videoMode) {
      setSpeaking(false);
    }
    setListening(true);
  };

  const handleResponseDone = (event) => {
    console.log("📍 Response done event:", event);
    // Add a small delay to ensure video has started before we clear speaking
    setTimeout(() => {
      // Only clear speaking state if we're actually speaking
      // This prevents premature clearing during the initial response
      if (speakingRef.current) {
        setSpeaking(false);
        setIsProcessing(false);
        console.log("📍 Response complete - cleared speaking state, button should be green");
      }
    }, 100);
  };

  const handleSpeechStarted = () => {
    console.log("\n🎤 USER SPEECH STARTED");
    
    setUserSpeaking(true); // Set user speaking state
    setIsProcessing(false); // Clear any processing state when user starts speaking

    // Immediately stop speaking and show interruption feedback
    if (speaking) {
      setSpeaking(false);
      setIsInterrupted(true);
      videoInterruptedRef.current = true;

      // Just pause video - don't destroy the stream
      if (streamVideoRef.current) {
        streamVideoRef.current.pause();
      }
      showIdleVideo();
      
      // Reset byte counter
      lastBytesReceivedRef.current = 0;

      setCurrentResponse("");
      setAudioLevels([]);

      // Reset interruption indicator after a moment
      setTimeout(() => setIsInterrupted(false), 1500);
    }

    setListening(true);
  };

  const handleSpeechStopped = () => {
    speechStoppedTimeRef.current = Date.now();
    setUserSpeaking(false); // Clear user speaking state
    setIsProcessing(true); // Set processing state after user stops speaking
    setListening(true);
  };

  const handleAssistantInterrupted = (event) => {
    console.log("🛑 Assistant interrupted event received:", event);
    
    // Mark that we're interrupted to prevent auto-resume
    videoInterruptedRef.current = true;
    
    // Just pause - don't reset currentTime to avoid frozen frame
    if (streamVideoRef.current) {
      streamVideoRef.current.pause();
    }
    showIdleVideo();
    
    // Reset byte counter
    lastBytesReceivedRef.current = 0;
    
    // Update UI
    setIsProcessing(false); // Clear processing state on interruption
    setIsInterrupted(true);
  };

  const handleModeChanged = (data) => {
    console.log("🎯 Mode changed:", data);
    setCurrentMode(data.current_mode);
    
    // Show welcome message as toast
    if (data.welcome_message) {
      showToast(data.welcome_message, "info");
    }
  };


  const handleModeStatus = (data) => {
    console.log("📊 Mode status:", data);
    setCurrentMode(data.current_mode);
  };

  const handleInitialState = (data) => {
    console.log("🏁 Initial state received:", data);
    // Update mode from backend
    setCurrentMode(data.mode || "visitor");
    
    // Update authentication state based on backend
    if (data.is_authenticated) {
      console.log("🏁 Backend confirms authentication, updating local state");
      setIsAuthenticated(true);
    }
    
    // Log for debugging
    console.log("🏁 Set initial state - mode:", data.mode, "backend auth:", data.is_authenticated, "local auth:", isAuthenticated, "checkingAuth:", checkingAuth);
  };

  const handleAuthComplete = (data) => {
    console.log("🔐 Auth complete from backend:", data);
    
    // Store JWT token if provided
    if (data.jwt_token) {
      console.log("🔐 Storing JWT token for admin mode");
      clientRef.current.setAuthToken(data.jwt_token);
      
      // CRITICAL: Store auth data in localStorage for persistence across handoffs
      // This handles the case where auth_complete comes from backend after handoff
      if (!data.restored) {  // Only store if this is a new auth, not a restored one
        const authData = {
          accessToken: data.jwt_token,  // Using jwt_token as access token
          idToken: data.jwt_token,
          refreshToken: data.refresh_token || '',  // May not have refresh token
          phone: data.phone || data.admin_name || '',
          tenantId: data.tenant_id,
          timestamp: Date.now()
        };
        localStorage.setItem('adminAuth', JSON.stringify(authData));
        console.log("🔐 Stored auth data in localStorage for persistence");
      }
      
      // Import AuthManager to store the token
      import('../utils/authManager.js').then(({ AuthManager }) => {
        const authManager = new AuthManager();
        authManager.setAuthData({
          idToken: data.jwt_token,
          user_id: data.user_id,
          admin_name: data.admin_name,
          tenantId: data.tenant_id
        });
        console.log("🔐 JWT token stored in AuthManager");
      });
    }
    
    // Update to admin mode after backend confirms authentication
    const targetMode = data.switch_to_mode || data.mode || "admin";
    setCurrentMode(targetMode);
    setIsAuthenticated(true);
    
    // Hide auth overlay if it was shown (for restored auth)
    if (data.restored && showAuthOverlay) {
      setShowAuthOverlay(false);
    }
    
    // Log for debugging
    console.log("🔐 Auth complete - switching to mode:", targetMode, "restored:", data.restored);
  };

  // Client-side authentication tool setup - DISABLED
  // All auth is now handled by the backend for security
  const setupClientSideAuthTools = () => {
    console.log("🔧 Client-side auth tools disabled - backend handles all auth");
  };

  // Authentication tool handler - DISABLED
  // All auth is now handled by the backend for security
  const handleAuthToolCall = async (toolCall) => {
    console.log(`🔧 Auth tool call intercepted but disabled: ${toolCall.name}`);
    // Do nothing - let backend handle all auth
  };

  // Send auth tools to backend via session update instead of config message
  const sendAuthToolsToBackend = () => {
    if (!clientRef.current || !clientRef.current.socket || clientRef.current.socket.readyState !== WebSocket.OPEN) {
      console.warn("⚠️ Cannot send auth tools - WebSocket not ready");
      return;
    }

    // Auth tools are now handled entirely by the backend auth agent
    // No need to define them in the frontend - the backend will handle all auth functions
    console.log("🔧 Auth tools are managed by backend auth agent");
  };

  // Build instructions that include authentication workflow
  const buildAuthInstructions = () => {
    // Auth instructions are now handled by the backend AuthAgent
    // The frontend no longer needs to provide auth-specific instructions
    return instructions || `You are a helpful voice assistant. Be conversational and helpful.`;
  };

  // User name extraction
  const extractUserName = (text) => {
    const namePhrases = [
      /my name is (\w+)/i,
      /i am (\w+)/i,
      /call me (\w+)/i,
      /i'm (\w+)/i,
      /name's (\w+)/i,
    ];

    let extractedName = "";
    for (const pattern of namePhrases) {
      const match = text.match(pattern);
      if (match) {
        extractedName = match[1];
        break;
      }
    }

    if (extractedName) {
      const formattedName =
        extractedName.charAt(0).toUpperCase() +
        extractedName.slice(1).toLowerCase();
      setUserName(formattedName);
      localStorage.setItem("userName", formattedName);
    }
  };

  const showStrategyChangeNotification = (newStrategy, fromDashboard) => {
    // You can implement a toast notification here
    console.log(
      `🎛️ Strategy changed to: ${newStrategy} ${
        fromDashboard ? "(from dashboard)" : ""
      }`
    );
  };

  // Audio visualization loop
  useEffect(() => {
    if (!speaking) {
      // Fade out audio levels when not speaking
      const fadeOut = () => {
        setAudioLevels((prev) => prev.map((level) => Math.max(0, level * 0.9)));
      };

      const interval = setInterval(fadeOut, 50);
      return () => clearInterval(interval);
    }
  }, [speaking]);

  useEffect(() => {
    if (videoMode && idleVideoRef.current) {
      playIdleVideo();
    }
  }, [videoMode]);


  // Connection/interruption toggle
  const toggleConnection = useCallback(async () => {
    console.log(
      `UI: toggleConnection called - connected=${connected}, connecting=${connecting}, speaking=${speaking}`
    );
    if (!connected) {
      console.log("UI: initiating connection");
      // Inline connect logic to avoid circular dependency
      if (connecting || connected) {
        console.log(`UI: connect() skipped (connecting=${connecting}, connected=${connected})`);
        return;
      }

      // Wait for auth check to complete
      if (checkingAuth) {
        console.log("UI: Waiting for auth check to complete before connecting...");
        // Wait up to 2 seconds for auth check
        let waitTime = 0;
        while (checkingAuth && waitTime < 2000) {
          await new Promise(resolve => setTimeout(resolve, 100));
          waitTime += 100;
        }
        console.log(`UI: Auth check complete after ${waitTime}ms`);
      }

      console.log("UI: connect() initiating");
      console.log(`🔑 Using UUID: ${userUuid}`);
      setConnecting(true);
      setError(null);
      
      // Reset session handled flag for new connection
      sessionHandledRef.current = false;

      try {
        await clientRef.current.connect(
          userUuid,
          instructions || "",
          toolStrategy,
          videoModeRef.current
        );
      } catch (error) {
        console.error("Connect error:", error);
        setError(error.toString());
        setConnecting(false);
      }
    } else if (speaking) {
      console.log("UI: manually interrupting assistant");
      clientRef.current.cancelResponse();
      setSpeaking(false);
      setCurrentResponse("");
      setAudioLevels([]);
      setIsInterrupted(true);
      setTimeout(() => setIsInterrupted(false), 1000);
    } else {
      console.log("UI: disconnecting client");
      // Inline disconnect logic
      clientRef.current.disconnect();
      if (audioContextRef.current) {
        // Only close if not already closed to avoid InvalidStateError
        if (audioContextRef.current.state !== "closed") {
          audioContextRef.current
            .close()
            .catch((err) => console.warn("AudioContext close error:", err));
        }
        audioContextRef.current = null;
      }
      setAudioLevels([]);
      
      // Clean up video if needed
      if (videoMode && (videoWebSocket || peerConnection)) {
        console.log("🎬 Cleaning up video on disconnect");
        // We'll handle cleanup after defining cleanupVideoStream
      }
      
      // Reset mute state on disconnect
      setIsMuted(false);
    }
  }, [connected, connecting, speaking, checkingAuth, userUuid, toolStrategy, videoMode, videoWebSocket, peerConnection, instructions]);

  // Update microphone state for overlay controls
  useEffect(() => {
    if (onMicrophoneStateChange) {
      onMicrophoneStateChange({
        connected,
        connecting,
        listening,
        speaking,
        userSpeaking,
        isProcessing,
        isInterrupted,
        toggleConnection
      });
    }
  }, [connected, connecting, listening, speaking, userSpeaking, isProcessing, isInterrupted, toggleConnection, onMicrophoneStateChange]);

  const connect = useCallback(async () => {
    if (connecting || connected) {
      console.log(
        `UI: connect() skipped (connecting=${connecting}, connected=${connected})`
      );
      return;
    }

    // Wait for auth check to complete
    if (checkingAuth) {
      console.log("UI: Waiting for auth check to complete before connecting...");
      // Wait up to 2 seconds for auth check
      let waitTime = 0;
      while (checkingAuth && waitTime < 2000) {
        await new Promise(resolve => setTimeout(resolve, 100));
        waitTime += 100;
      }
      console.log(`UI: Auth check complete after ${waitTime}ms`);
    }

    console.log("UI: connect() initiating");
    console.log(`🔑 Using UUID: ${userUuid}`);
    console.log(`🔑 Authenticated: ${isAuthenticated}`);
    setConnecting(true);
    setError(null);
    
    // Reset session handled flag for new connection
    sessionHandledRef.current = false;

    // Build auth-aware instructions that include tools
    const authInstructions = buildAuthInstructions();

    try {
      await clientRef.current.connect(
        userUuid,
        authInstructions,
        toolStrategy,
        videoModeRef.current
      );
    } catch (error) {
      console.error("Connect error:", error);
      setError(error.toString());
      setConnecting(false);
    }
  }, [connecting, connected, checkingAuth, userUuid, toolStrategy, isAuthenticated, currentMode]);

  const disconnect = useCallback(() => {
    console.log("UI: disconnect() called");
    clientRef.current.disconnect();
    if (audioContextRef.current) {
      // Only close if not already closed to avoid InvalidStateError
      if (audioContextRef.current.state !== "closed") {
        audioContextRef.current
          .close()
          .catch((err) => console.warn("AudioContext close error:", err));
      }
      audioContextRef.current = null;
    }
    setAudioLevels([]);
    
    // Clean up video if needed
    if (videoMode && (videoWebSocket || peerConnection)) {
      console.log("🎬 Cleaning up video on disconnect");
      cleanupVideoStream();
    }
    
    // Reset mute state on disconnect
    setIsMuted(false);
  }, [videoMode, videoWebSocket, peerConnection]);
  
  // Simple mute toggle
  const toggleMute = () => {
    if (!connected || !clientRef.current || !clientRef.current.audioStream) return;
    
    const newMuteState = !isMuted;
    setIsMuted(newMuteState);
    
    // Simply enable/disable audio tracks
    clientRef.current.audioStream.getAudioTracks().forEach(track => {
      track.enabled = !newMuteState;
    });
    
    showToast(`Microphone ${newMuteState ? 'muted' : 'unmuted'}`, 'info');
  };

  // Check for admin authentication triggers in user speech
  // NOTE: This is kept for backward compatibility but the primary intent detection
  // now happens on the backend via AI-powered intent detection
  const checkForAdminAuthTriggers = (text) => {
    // This function is no longer actively used since we rely on backend intent detection
    // Keeping it here for reference only
  };

  // Authentication success handler
  const handleAuthSuccess = async (authData) => {
    console.log('🔑 Authentication successful:', authData);
    
    // Store auth data in authManager (AdminAuth already stored in localStorage)
    authManager.setAuthData(authData);
    
    // Update authentication state
    setIsAuthenticated(true);
    setShowAuthOverlay(false);
    
    // Switch to admin mode after successful authentication
    setCurrentMode('admin');
    
    showToast('Admin authentication successful!', 'success');
    
    // Auth success event is already sent by AdminAuth component
    // Just show the toast message
    showToast('Switching to admin mode...', 'info');
  };

  // Authentication cancel handler
  const handleAuthCancel = () => {
    setShowAuthOverlay(false);
    
    // Notify backend of auth cancellation
    if (clientRef.current && clientRef.current.connected) {
      clientRef.current.sendEvent({
        type: "auth.event",
        event_type: "auth_cancelled",  // Changed from 'event' to 'event_type'
        data: {}
      });
    }
    
    showToast('Authentication cancelled', 'info');
  };

  // Logout handler
  const handleLogout = () => {
    // Clear both authManager and localStorage
    authManager.clearAuth();
    localStorage.removeItem('adminAuth');
    setIsAuthenticated(false);
    
    // Notify backend of logout
    if (clientRef.current && clientRef.current.connected) {
      clientRef.current.sendEvent({
        type: 'auth.event',
        event_type: 'auth_logout',
        data: {}
      });
    }
    
    // Switch back to visitor mode if currently in admin mode
    if (currentMode === 'admin') {
      switchMode('visitor');
    }
    
    showToast('Logged out successfully', 'info');
  };

  // Mode switching with authentication check
  const switchMode = (targetMode) => {
    console.log('🔄 switchMode called:', {
      targetMode,
      currentMode,
      connected,
      isAuthenticated,
      hasClient: !!clientRef.current
    });
    
    if (!connected || !clientRef.current) {
      showToast('Please connect first', 'error');
      return;
    }
    
    if (targetMode === currentMode) {
      showToast(`Already in ${targetMode} mode`, 'info');
      return;
    }

    // Check authentication for admin mode
    if (targetMode === 'admin' && !isAuthenticated) {
      console.log('🔑 Authentication required for admin mode');
      showToast('Admin authentication required', 'info');
      setShowAuthOverlay(true);
      return;
    }
    
    // Send mode change request
    const success = clientRef.current.sendModeChangeRequest(targetMode);
    if (success) {
      showToast(`Switching to ${targetMode} mode...`, 'info');
    } else {
      showToast('Failed to switch mode', 'error');
    }
  };

  // Get status text (always returns a string)
  const getStatusText = () => {
    let status;
    if (error) {
      status = error;
    } else if (connecting) {
      status = "Connecting...";
    } else if (!connected) {
      status = "Ready to chat";
    } else if (isInterrupted) {
      status = "Interrupted - listening...";
    } else if (listening) {
      status = "Listening...";
    } else if (speaking) {
      status = currentResponse ? "AI speaking..." : "AI thinking...";
    } else {
      status = "Connected";
    }
    return typeof status === "string" ? status : JSON.stringify(status);
  };

  // Get status color
  const getStatusColor = () => {
    if (error) return "text-red-500";
    if (isInterrupted) return "text-yellow-600";
    if (listening) return "text-green-600";
    if (speaking) return "text-blue-600";
    if (connected) return "text-emerald-600";
    return "text-gray-500";
  };

  const StrategyDisplay = () => {
    if (!connected) return null;
  };

  const strategyLabels = {
    auto: "Auto",
    conservative: "Minimal",
    aggressive: "Comprehensive",
    none: "Direct Only",
    required: "Always Search",
  };

  const strategyColors = {
    auto: "text-blue-400",
    conservative: "text-green-400",
    aggressive: "text-purple-400",
    none: "text-gray-400",
    required: "text-red-400",
  };

  // Toast functions
  const showToast = (message, type = "info") => {
    setToast({ message, type });

    // Clear after 3 seconds
    setTimeout(() => setToast(null), 3000);
  };

  // Clear memory functions
  const handleClearMemory = async () => {
    console.log("🗑️ Clear memory button clicked!");
    console.log("🔍 User UUID:", userUuid);

    try {
      console.log(`📤 Sending request to ${getBackendUrl()}/memory/clear`);

      const response = await authManager.authenticatedFetch(`${getBackendUrl()}/memory/clear`, {
        method: "POST",
        body: JSON.stringify({ uuid: userUuid }),
      });

      console.log("📥 Response status:", response.status);
      console.log("📥 Response ok:", response.ok);

      if (!response.ok) {
        const errorText = await response.text();
        console.error("❌ Response error:", errorText);
        throw new Error(
          `Failed to clear memory: ${response.status} - ${errorText}`
        );
      }

      const result = await response.json();
      console.log("✅ Clear memory result:", result);

      const { cleared } = result;

      if (cleared.total_chars > 0) {
        showToast(
          `Memory cleared: ${cleared.session_messages} messages, ${cleared.total_chars} characters`,
          "success"
        );
      } else {
        showToast("No memory to clear", "info");
      }

      // Clear local messages as well
      setMessages([]);
      setCurrentResponse("");
    } catch (error) {
      console.error("❌ Clear memory error:", error);
      showToast(`Failed to clear memory: ${error.message}`, "error");
    }
  };

  const createVideoStream = async () => {
    if (videoWebSocket || peerConnection) {
      console.log("🎬 Video stream already exists, skipping creation");
      return;
    }

    // 1) start idle immediately
    playIdleVideo();

    // 2) open existing D-ID WebSocket
    const wsUrl = `${getBackendUrl().replace(
      "http",
      "ws"
    )}/video/stream?user_uuid=${userUuid}`;
    console.log("🎬 Connecting to video WebSocket:", wsUrl);
    console.log("🎬 User UUID:", userUuid);
    
    const ws = new WebSocket(wsUrl);
    
    // Store WebSocket reference immediately to prevent duplicate connections
    setVideoWebSocket(ws);
    
    ws.onopen = () => {
      console.log(
        "🎬 Video WebSocket connected, sending create_stream request"
      );
      ws.send(JSON.stringify({ type: "create_stream" }));
    };

    ws.onerror = (error) => {
      console.error("🎬 Video WebSocket error:", error);
      cleanupVideoStream();
    };

    ws.onmessage = async (evt) => {
      const msg = JSON.parse(evt.data);
      console.log("🎬 Video WS message:", msg.type, msg);
      
      // Log all message types to see what we get when video ends
      if (!["stream_created", "did_talk_generated", "stream_interrupted"].includes(msg.type)) {
        console.log("🎬 📨 Other message type received:", msg.type);
      }
      
      if (msg.type === "stream_created") {
        console.log("🎬 Stream created successfully with ID:", msg.stream_id);
        // standard two-step WebRTC handshake
        const pc = new RTCPeerConnection({ iceServers: msg.ice_servers });
        setPeerConnection(pc);
        pc.addEventListener("iceconnectionstatechange", () => {
          console.log("🧊 ICE connection state:", pc.iceConnectionState);
          if (pc.iceConnectionState === "failed") {
            // Only cleanup on permanent failure, not temporary disconnection
            console.log("🧊 ICE connection failed - cleaning up");
            cleanupVideoStream();
          }
        });

        pc.addEventListener("track", (event) => {
          console.log("🎬 Track received:", event.track.kind, event.track.id);

          if (event.track.kind === "video") {
            setStreamVideo(event.streams[0]);

            // Clear any existing interval
            if (statsIntervalRef.current) {
              clearInterval(statsIntervalRef.current);
            }

            console.log("📊 Starting stats monitoring for video track");
            console.log("📊 Initial video state:", {
              streamVideoOpacity: streamVideoRef.current.style.opacity,
              idleVideoOpacity: idleVideoRef.current.style.opacity,
              streamVideoSrc: !!streamVideoRef.current.srcObject
            });
            let statsCount = 0;

            // Capture the pc reference in a closure
            const peerConnectionRef = pc; // Capture it here

            statsIntervalRef.current = setInterval(async () => {
              statsCount++;

              // Use captured reference
              if (
                !peerConnectionRef ||
                peerConnectionRef.connectionState === "closed" ||
                peerConnectionRef.connectionState === "failed"
              ) {
                console.log(
                  `📊 Stats: PC in bad state: ${peerConnectionRef?.connectionState}, stopping monitoring`
                );
                clearInterval(statsIntervalRef.current);
                // Go back to idle when connection dies
                showIdleVideo();
                return;
              }

              try {
                const stats = await peerConnectionRef.getStats(event.track);
                stats.forEach((report) => {
                  if (
                    report.type === "inbound-rtp" &&
                    report.kind === "video"
                  ) {
                    const currentBytes = report.bytesReceived;
                    const videoIsPlaying = currentBytes > lastBytesReceivedRef.current;

                    // Log every 10th check to avoid spam
                    if (statsCount % 10 === 0) {
                      console.log(
                        `📊 Stats check #${statsCount}: bytes=${currentBytes}, lastBytes=${lastBytesReceivedRef.current}, playing=${videoIsPlaying}`
                      );
                      console.log(
                        `📊 Current opacity: stream=${streamVideoRef.current?.style?.opacity || 'null'}, idle=${idleVideoRef.current?.style?.opacity || 'null'}`
                      );
                    }

                    // Simple approach with interruption handling
                    
                    if (videoIsPlaying && currentBytes > 0) {
                      // Video data is flowing
                      if (!videoInterruptedRef.current) {
                        // Show stream video
                        showStreamVideo();
                        
                        // Play if paused
                        if (streamVideoRef.current && streamVideoRef.current.paused) {
                          console.log("📺 Attempting to play paused video");
                          streamVideoRef.current.play().then(() => {
                            console.log("✅ Video play() succeeded");
                          }).catch(err => {
                            console.error("❌ Failed to play:", err);
                          });
                        }
                      } else {
                        console.log("📺 Video data flowing but interrupted - keeping paused");
                      }
                    } else if (!videoIsPlaying && lastBytesReceivedRef.current > 0) {
                      // No video data - show idle
                      showIdleVideo();
                    }

                    lastBytesReceivedRef.current = currentBytes;
                  }
                });
              } catch (error) {
                console.error("📊 Stats error:", error);
              }
            }, 500);
          }
        });

        await pc.setRemoteDescription(msg.offer);
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);

        ws.send(
          JSON.stringify({
            type: "start_connection",
            answer: answer,
          })
        );
        // Mark video as ready after a delay
        setTimeout(() => {
          videoStreamReadyRef.current = true;
          console.log("🎬 Video stream marked as ready");
        }, 100); // Give D-ID time to set up
      } else if (msg.type === "did_talk_generated") {
        console.log("🎬 D-ID video generated event:", msg);
        
        // Clear interrupted flag - we have new video content
        console.log("🎬 Clearing interrupted flag for new D-ID video");
        videoInterruptedRef.current = false;
        // Don't set speaking here - let the video onplaying event handle it
        setIsProcessing(true); // Show processing until video actually plays
      } else if (msg.type === "stream_interrupted") {
        console.log("🛑 Stream interrupted by user speech:", msg);
        
        // Mark as interrupted
        videoInterruptedRef.current = true;
        
        // Always handle interruption when we receive this event
        console.log("🛑 Stopping video playback on stream interruption");
        
        // Pause video but don't reset time
        if (streamVideoRef.current) {
          streamVideoRef.current.pause();
        }
        
        // Reset last bytes for stats monitoring
        lastBytesReceivedRef.current = 0;
        
        // Show idle video
        showIdleVideo();
        playIdleVideo(); // Ensure idle video is playing
        
        // Update UI state
        setIsInterrupted(true);
      } else if (msg.type === "video_complete" || msg.type === "response_complete" || msg.type === "did_talk_complete") {
        console.log("🎬 Video/Response complete:", msg);
        // The showIdleVideo will handle clearing the speaking state
        showIdleVideo();
      }
    };

    ws.onclose = (event) => {
      console.log("🔌 video socket closed", event.code, event.reason);
      setVideoWebSocket(null);
      setPeerConnection(null);

      // Only reconnect if video mode is still enabled
      if (videoMode) {
        console.log("🔄 Reconnecting video socket...");
        setTimeout(() => {
          createVideoStream();
        }, 2000);
      }
    };
  };

  const cleanupVideoStream = () => {
    console.log("🧹 Cleaning up video stream");
    console.log("🧹 Cleanup state:", {
      hasStreamVideo: !!streamVideoRef.current,
      isStreamPaused: streamVideoRef.current?.paused,
      hasStatsInterval: !!statsIntervalRef.current
    });

    // Stop stats monitoring
    if (statsIntervalRef.current) {
      clearInterval(statsIntervalRef.current);
      statsIntervalRef.current = null;
    }
    lastBytesReceivedRef.current = 0;

    // Pause and hide stream video
    if (streamVideoRef.current) {
      streamVideoRef.current.pause();
      streamVideoRef.current.srcObject = null;
      streamVideoRef.current.style.opacity = "0";
    }

    // Show idle video
    if (idleVideoRef.current) {
      idleVideoRef.current.style.opacity = "1";
      playIdleVideo(); // Ensure it's playing
    }

    // Close connections
    if (peerConnection) {
      peerConnection.close();
      setPeerConnection(null);
    }

    if (videoWebSocket) {
      videoWebSocket.close();
      setVideoWebSocket(null);
    }

    console.log("🧹 Video cleanup complete - back to idle");
  };

  const handleAppendSummary = async () => {
    const info = window.prompt(
      "Enter information to append to memory summary:"
    );
    if (!info) return;
    try {
      const res = await authManager.authenticatedFetch(`${getBackendUrl()}/memory/summary`, {
        method: "POST",
        body: JSON.stringify({ uuid: userUuid, info }),
      });
      if (!res.ok) {
        const errText = await res.text();
        throw new Error(`Status ${res.status}: ${errText}`);
      }
      showToast("Memory summary updated", "success");
    } catch (error) {
      console.error("❌ Append summary error:", error);
      showToast(`Error appending summary: ${error.message}`, "error");
    }
  };

  // Confirmation wrapper
  const confirmClearMemory = async () => {
    if (isClearing) {
      console.log("🚫 Clear memory already in progress, ignoring click");
      return;
    }

    try {
      const messageCount = messages.length;
      const confirmed = window.confirm(
        `This will permanently delete all conversation history${
          messageCount > 0 ? ` (${messageCount} messages)` : ""
        }.\n\nAre you sure you want to continue?`
      );

      if (confirmed) {
        setIsClearing(true);
        await handleClearMemory();
        setTimeout(() => setIsClearing(false), 1000);
      }
    } catch (error) {
      console.error("Error in confirmation:", error);
      showToast("Error checking memory status", "error");
      setIsClearing(false);
    }
  };

  // Toast Component
  const Toast = ({ message, type }) => {
    const bgColor =
      type === "error"
        ? "bg-red-500"
        : type === "success"
        ? "bg-green-500"
        : "bg-blue-500";

    return (
      <div
        className={`fixed top-4 right-4 ${bgColor} text-white px-4 py-2 rounded-lg shadow-lg z-50 transition-all duration-300`}
      >
        {message}
      </div>
    );
  };

  // If in background mode, only render the video elements
  if (backgroundMode) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: 'black',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: 0.4,
      }}>
        {/* Idle video element - always visible when no stream */}
        <video
          ref={idleVideoRef}
          id="idle-video-element"
          src="https://airspace-assets.s3.us-east-2.amazonaws.com/envs/shared_assets/personas/miguel_silent_small.mp4"
          preload="auto"
          autoPlay
          loop
          playsInline
          muted
          style={{
            position: "absolute",
            width: "100%",
            height: "100%",
            objectFit: "cover",
            backgroundColor: "black",
            opacity: 1,
            filter: 'brightness(1.1) contrast(1.2) saturate(1.1) invert(1)'
          }}
        />

        {/* Stream video element - visible when streaming */}
        <video
          ref={streamVideoRef}
          id="stream-video-element"
          autoPlay
          playsInline
          muted={false}
          style={{
            position: "absolute",
            width: "100%",
            height: "100%",
            objectFit: "cover",
            backgroundColor: "black",
            opacity: 0,
            filter: 'brightness(1.1) contrast(1.2) saturate(1.1) invert(1)'
          }}
        />
      </div>
    );
  }

  // Get microphone state based on current conditions
  const getMicrophoneState = () => {
    if (!connected) return "off";
    if (isMuted) return "off"; // Grey for muted state
    if (connecting) return "processing";
    if (speaking) return "speaking"; // Purple while assistant is speaking
    if (userSpeaking) return "recording"; // Red while user is talking
    if (isProcessing) return "processing"; // Orange after user stops talking, before video plays
    if (listening && !speaking) return "listening"; // Green when waiting/listening
    return "listening"; // Default to listening state
  };

  const getMicrophoneStyles = () => {
    const micState = getMicrophoneState();
    // Recording uses scale animation, all other active states use opacity-only animation
    let pulseClass = "";
    if (micState === "recording") {
      pulseClass = "animate-pulse-scale"; // This one grows and shrinks
    } else if (micState !== "off") {
      pulseClass = "animate-pulse"; // This one only changes opacity
    }

    const baseStyles = `w-12 h-12 rounded-full flex items-center justify-center cursor-pointer transition-all duration-300 shadow-lg relative overflow-hidden ${pulseClass}`;
    const stateSpecificStyles = {
      off: "bg-gradient-to-br from-slate-500 to-slate-700 text-white hover:from-slate-400 hover:to-slate-600 shadow-slate-500/30", // Grey for mute
      listening: "bg-gradient-to-br from-emerald-500 to-emerald-600 text-white shadow-emerald-500/40", // Green for waiting/listening
      recording: "bg-gradient-to-br from-red-500 to-red-600 text-white shadow-red-500/40", // Red while user talking
      processing: "bg-gradient-to-br from-amber-500 to-orange-500 text-white shadow-amber-500/40", // Orange while processing
      speaking: "bg-gradient-to-br from-violet-500 to-purple-600 text-white shadow-violet-500/40", // Purple while speaking
    };
    return `${baseStyles} ${stateSpecificStyles[micState]}`;
  };

  const getMicrophoneIcon = () => {
    const micState = getMicrophoneState();
    switch (micState) {
      case "off":
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
            <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
            <line x1="12" x2="12" y1="19" y2="22" />
            <line x1="1" x2="23" y1="1" y2="23" stroke="currentColor" strokeWidth="2" />
          </svg>
        );
      case "speaking":
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
            <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
            <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
          </svg>
        );
      default:
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
            <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
            <line x1="12" x2="12" y1="19" y2="22" />
          </svg>
        );
    }
  };

  const getBottomAnimationStyle = () => {
    const micState = getMicrophoneState();
    const style = { colorClasses: "", animationDuration: "1.5s", visible: false };
    switch (micState) {
      case "listening":
        style.colorClasses = "bg-gradient-to-t from-emerald-500 to-emerald-300";
        style.visible = true;
        break;
      case "recording":
        style.colorClasses = "bg-gradient-to-t from-red-500 to-red-300";
        style.animationDuration = "0.75s";
        style.visible = true;
        break;
      case "processing":
        style.colorClasses = "bg-gradient-to-t from-amber-500 to-orange-300";
        style.visible = true;
        break;
      case "speaking":
        style.colorClasses = "bg-gradient-to-t from-violet-500 to-purple-300";
        style.visible = true;
        break;
      default:
        style.visible = false;
        break;
    }
    return style;
  };

  const getParticleColor = () => {
    const micState = getMicrophoneState();
    const colors = {
      off: "bg-slate-300",
      listening: "bg-emerald-400",
      recording: "bg-red-400",
      processing: "bg-amber-400",
      speaking: "bg-violet-400",
    };
    return colors[micState] || "bg-slate-300";
  };

  const bottomAnimationStyle = getBottomAnimationStyle();

  // Render minimal UI in background mode
  if (backgroundMode) {
    return null; // The parent LiveOverlay will handle all UI
  }

  return (
    <div className="relative w-full h-screen overflow-hidden bg-black">
      {/* Add custom styles */}
      <style>{`
        @keyframes float { 
          0%, 100% { transform: translateY(0px) rotate(0deg); } 
          50% { transform: translateY(-20px) rotate(180deg); } 
        }
        .wave-bar { 
          animation-name: wave; 
          animation-timing-function: ease-in-out; 
          animation-iteration-count: infinite; 
          animation-duration: var(--wave-duration, 1.5s); 
        }
        @keyframes wave { 
          0%, 100% { transform: scaleY(1); } 
          50% { transform: scaleY(2.5); }
        }
        @keyframes pulse-opacity { 
          0%, 100% { opacity: 0.3; } 
          50% { opacity: 0.8; } 
        }
        @keyframes pulse-scale { 
          0%, 100% { opacity: 0.3; transform: scale(1); } 
          50% { opacity: 0.8; transform: scale(1.2); } 
        }
        .animate-pulse {
          animation: pulse-opacity 1s ease-in-out infinite;
        }
        .animate-pulse-scale {
          animation: pulse-scale 1s ease-in-out infinite;
        }
      `}</style>

      {/* Video Background */}
      {videoMode && (
        <div className="absolute inset-0">
          <video
            ref={idleVideoRef}
            className="absolute inset-0 w-full h-full object-cover"
            autoPlay
            loop
            muted
            playsInline
            style={{ opacity: 1, transition: "opacity 0.5s ease-in-out" }}
          >
            <source src="https://airspace-assets.s3.us-east-2.amazonaws.com/envs/shared_assets/personas/miguel_silent_small.mp4" type="video/mp4" />
          </video>
          <video
            ref={streamVideoRef}
            className="absolute inset-0 w-full h-full object-cover"
            autoPlay
            playsInline
            style={{ opacity: 0, transition: "opacity 0.5s ease-in-out" }}
          />
          <div className="absolute inset-0 bg-black bg-opacity-20 pointer-events-none" />
        </div>
      )}

      {/* Header */}
      <header className="absolute top-0 left-0 right-0 z-30 flex justify-between items-center p-6 md:p-8">
        <div className="flex items-center">
          <span className="text-white text-2xl font-light">mobeus</span>
        </div>
        <nav className="flex items-center space-x-6 md:space-x-8">
          <a href="#" className="text-white/80 text-sm hover:text-white transition-colors">
            Who are we
          </a>
          <a href="#" className="text-white/80 text-sm hover:text-white transition-colors">
            Why we exist
          </a>
          <a href="#" className="text-white/80 text-sm hover:text-white transition-colors">
            What is a tele
          </a>
          <div className="border border-white rounded-full px-4 py-1.5 bg-white text-black text-sm">
            tele
          </div>
          <button
            onClick={toggleConnection}
            disabled={connecting}
            className={getMicrophoneStyles()}
            aria-label={`Microphone ${getMicrophoneState()}`}
            type="button"
          >
            <div className="absolute inset-1 rounded-full bg-white/10 blur-sm" />
            <div className="relative z-10">{getMicrophoneIcon()}</div>
          </button>
        </nav>
      </header>

      {/* Main UI Content */}
      {showMessages && !videoMode && (
        <div className="absolute inset-0 z-20 transition-opacity duration-500 ease-in-out">
          <div className="flex h-full max-w-6xl mx-auto">
            {/* Left Side - User Messages */}
            <div className="flex-1 p-8 backdrop-blur-sm bg-black/20">
              <div className="h-full flex flex-col">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-white text-lg font-light drop-shadow-lg">Conversation</h2>
                </div>
                <div className="flex-1 space-y-3 overflow-y-auto">
                  {messages.map((message, index) => (
                    <div key={`msg-${index}`} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`rounded-lg px-4 py-2 max-w-md backdrop-blur-sm shadow-lg ${
                        message.role === 'user' 
                          ? 'bg-blue-500/80 text-white' 
                          : 'bg-white/80 text-gray-800'
                      }`}>
                        <p className="text-sm">{message.text}</p>
                        <p className={`text-xs mt-1 opacity-75 ${
                          message.role === 'user' ? 'text-blue-100' : 'text-gray-500'
                        }`}>
                          {new Date(message.timestamp).toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Side - Assistant Messages */}
            <div className="flex-1 p-8 backdrop-blur-sm bg-black/20">
              <div className="h-full flex flex-col">
                <h2 className="text-white text-lg font-light drop-shadow-lg mb-4">Mobeus AI</h2>
                <div className="flex-1 space-y-3 overflow-y-auto">
                  {messages
                    .filter((msg) => msg.role === "assistant")
                    .map((message, index) => (
                      <div key={`assistant-${index}`} className="flex justify-start">
                        <div className="bg-white/80 text-gray-800 rounded-lg px-4 py-2 max-w-md backdrop-blur-sm shadow-lg">
                          <p className="text-sm">{message.text}</p>
                          <p className="text-gray-500 text-xs mt-1 opacity-75">
                            {new Date(message.timestamp).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </p>
                        </div>
                      </div>
                    ))}
                  {currentResponse && (
                    <div className="flex justify-start">
                      <div className="bg-violet-500/80 text-white rounded-lg px-4 py-2 max-w-md backdrop-blur-sm shadow-lg animate-pulse">
                        <p className="text-sm">{currentResponse}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}



      {toast && <Toast message={toast.message} type={toast.type} />}
      
      {/* Admin Authentication Overlay */}
      {showAuthOverlay && (
        <AdminAuth
          ref={adminAuthRef}
          onSuccess={handleAuthSuccess}
          onCancel={handleAuthCancel}
          tenantId={window.location.hostname}
          onPhoneSubmitted={(phoneNumber) => {
            // Notify backend that phone was submitted
            if (clientRef.current && clientRef.current.connected) {
              clientRef.current.sendEvent({
                type: "auth.event",
                event: "phone_submitted",
                data: { phone_number: phoneNumber }
              });
            }
          }}
          onOtpSent={() => {
            // Notify backend that OTP was sent
            if (clientRef.current && clientRef.current.connected) {
              clientRef.current.sendEvent({
                type: "auth.event",
                event: "otp_sent",
                data: {}
              });
            }
          }}
          onOtpSubmitted={(code) => {
            // Notify backend that OTP was submitted for verification
            if (clientRef.current && clientRef.current.connected) {
              clientRef.current.sendEvent({
                type: "auth.event",
                event: "otp_submitted",
                data: { code }
              });
            }
          }}
          onAuthFailed={(error) => {
            // Notify backend of auth failure
            if (clientRef.current && clientRef.current.connected) {
              clientRef.current.sendEvent({
                type: "auth.event",
                event: "auth_failed",
                data: { error: error.message || error }
              });
            }
          }}
        />
      )}
    </div>
  );
}
